import { Fragment, type ReactNode } from 'react';
import type { Invoice, InvoiceSummary } from '@arcnet/contracts';
import { Badge } from '../../components/ui/Primitives.js';
import { formatMoney, formatUnitPrice } from '../../lib/format.js';

/**
 * An invoice as it prints (docs/28 B2; doc 15 §8): the same document for the
 * client and for staff, from the parties as they were on the day it was issued.
 * "Tax invoice" only when the issuer has a VAT number to put on it.
 */

/** A calendar date, YYYY-MM-DD, as a person reads it. */
export const docDate = (d: string | null): string =>
  d ? new Intl.DateTimeFormat('en-ZA', { timeZone: 'UTC', dateStyle: 'medium' })
    .format(new Date(`${d}T00:00:00Z`)) : '—';

/** Prints the document on the page and nothing else (app.css, arc-printing). */
export function printDocument(): void {
  const root = document.documentElement;
  const done = () => {
    root.classList.remove('arc-printing');
    window.removeEventListener('afterprint', done);
  };
  root.classList.add('arc-printing');
  window.addEventListener('afterprint', done);
  window.print();
}

export function InvoiceBadge({ invoice: i }: { invoice: Pick<InvoiceSummary, 'status' | 'overdue'> }) {
  if (i.overdue) return <Badge tone="danger">overdue</Badge>;
  switch (i.status) {
    case 'draft': return <Badge tone="muted">draft</Badge>;
    case 'issued': return <Badge tone="warn">unpaid</Badge>;
    case 'part_paid': return <Badge tone="warn">part paid</Badge>;
    case 'paid': return <Badge tone="ok">paid</Badge>;
    case 'credited': return <Badge tone="muted">credited</Badge>;
  }
}

const vatLabel = (treatment: string, rate: string): string =>
  treatment === 'exempt' ? 'exempt'
    : treatment === 'zero_rated' ? '0%'
    : `${(Number(rate) * 100).toFixed(0)}%`;

const lines = (text: string | null | undefined): string[] =>
  (text ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

export function InvoiceDocument({ invoice: i, creditNoteAction }: {
  invoice: Invoice;
  /** A control beside each credit note: its PDF. */
  creditNoteAction?: (note: Invoice['credit_notes'][number]) => ReactNode;
}) {
  const issuer = i.issuer;
  const customer = i.customer;
  const title = i.status === 'draft' ? 'Draft invoice' : issuer?.vat_number ? 'Tax invoice' : 'Invoice';
  const bank = issuer?.bank ?? null;

  return (
    <div className="arc-card arc-printable">
      <div className="arc-card-body">
        <div className="arc-row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="arc-subtle" style={{ fontSize: 12.5 }}>{issuer?.name}</div>
            <h2 className="arc-h2" style={{ marginTop: 2 }}>{title}</h2>
          </div>
          <div className="arc-spacer" />
          <div style={{ textAlign: 'right' }}>
            <div className="arc-mono" style={{ fontWeight: 600 }}>{i.number ?? 'Not issued'}</div>
            <InvoiceBadge invoice={i} />
          </div>
        </div>

        <div className="arc-grid arc-grid-2" style={{ marginTop: 16 }}>
          <div className="arc-doc-party">
            <div className="arc-label">From</div>
            {issuer && (
              <>
                <div style={{ fontWeight: 600 }}>{issuer.registered_name}</div>
                {lines(issuer.address).map((l, n) => <div key={n}>{l}</div>)}
                {issuer.registration_number && <div>Registration {issuer.registration_number}</div>}
                {issuer.vat_number && <div>VAT {issuer.vat_number}</div>}
                {(issuer.email || issuer.phone) && <div>{[issuer.email, issuer.phone].filter(Boolean).join(', ')}</div>}
              </>
            )}
          </div>
          <div className="arc-doc-party">
            <div className="arc-label">To</div>
            {customer && (
              <>
                <div style={{ fontWeight: 600 }}>{customer.name}</div>
                {lines(customer.address).map((l, n) => <div key={n}>{l}</div>)}
                {customer.vat_number && <div>VAT {customer.vat_number}</div>}
              </>
            )}
          </div>
        </div>

        <dl className="arc-kv" style={{ marginTop: 16 }}>
          <dt>Date</dt><dd>{docDate(i.issue_date)}</dd>
          <dt>Due</dt><dd>{docDate(i.due_date)}</dd>
          {i.customer_reference && <><dt>Your reference</dt><dd>{i.customer_reference}</dd></>}
          {i.period_start && <><dt>Period</dt><dd>{docDate(i.period_start)} to {docDate(i.period_end)}</dd></>}
        </dl>

        <div className="arc-table-wrap" style={{ marginTop: 16 }}>
          <table className="arc-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="arc-td-right">Quantity</th>
                <th className="arc-td-right">Unit price</th>
                <th className="arc-td-right">VAT</th>
                <th className="arc-td-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {i.lines.map((l) => (
                <tr key={l.position}>
                  <td>{l.description}</td>
                  <td className="arc-td-right arc-num">{l.quantity}</td>
                  <td className="arc-td-right arc-num">{formatUnitPrice(l.unit_price, i.currency)}</td>
                  <td className="arc-td-right arc-subtle">{vatLabel(l.vat_treatment, l.vat_rate)}</td>
                  <td className="arc-td-right arc-num">{formatMoney(l.amount, i.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="arc-kv arc-doc-totals">
          <dt>Subtotal</dt><dd className="arc-num">{formatMoney(i.subtotal, i.currency)}</dd>
          {i.vat_breakdown.map((v) => (
            <Fragment key={`${v.treatment}-${v.rate}`}>
              <dt>VAT {vatLabel(v.treatment, v.rate)} on {formatMoney(v.net, i.currency)}</dt>
              <dd className="arc-num">{formatMoney(v.vat, i.currency)}</dd>
            </Fragment>
          ))}
          <dt>Total</dt><dd className="arc-num" style={{ fontWeight: 600 }}>{formatMoney(i.total, i.currency)}</dd>
          {Number(i.amount_credited) > 0 && (
            <><dt>Credited</dt><dd className="arc-num">-{formatMoney(i.amount_credited, i.currency)}</dd></>
          )}
          {Number(i.amount_paid) > 0 && (
            <><dt>Paid</dt><dd className="arc-num">-{formatMoney(i.amount_paid, i.currency)}</dd></>
          )}
          {i.status !== 'draft' && (
            <><dt>Amount due</dt><dd className="arc-num" style={{ fontWeight: 600 }}>{formatMoney(i.amount_due, i.currency)}</dd></>
          )}
        </dl>

        {(i.credit_notes.length > 0 || i.payments.length > 0) && (
          <div className="arc-doc-note">
            {i.credit_notes.map((cn) => (
              <div key={cn.number}>
                Credit note {cn.number}, {docDate(cn.issue_date)}: {formatMoney(cn.total, i.currency)}. {cn.reason}
                {creditNoteAction && <> {creditNoteAction(cn)}</>}
              </div>
            ))}
            {i.payments.map((p, n) => (
              <div key={p.receipt_number ?? n}>
                Payment {p.receipt_number ?? ''}, {docDate(p.received_on)}: {formatMoney(p.amount, i.currency)}
                {p.reference ? ` (${p.reference})` : ''}
              </div>
            ))}
          </div>
        )}

        {bank && i.number && Number(i.amount_due) > 0 && (
          <div className="arc-doc-note">
            Pay by EFT to {bank.account_name}, {bank.name}, account {bank.account_number},
            branch {bank.branch_code}, with {i.number} as the reference.
          </div>
        )}
        {i.notes && <div className="arc-doc-note">{i.notes}</div>}
      </div>
    </div>
  );
}
